import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Artwork } from '../types/artworksData';

type CollectionSearchPattern = {
    regex: RegExp;
    buildCollectionId?: (match: RegExpMatchArray) => string;
};

type ExternalCollectionSearch = {
    collectionId: string;
    qf: string[];
};

const COLLECTION_SEARCH_PATTERNS: Record<string, CollectionSearchPattern[]> = {
    europeana: [
        { regex: /\/galleries\/(\d+)(?=-|[/?#]|$)/i },
        {
            regex: /\/collections\/organi[sz]ation\/(\d+)(?:[-/?#]|$)/i,
            buildCollectionId: (match) => `organization:${match[1]}`,
        },
    ],
    sketchfab: [
        { regex: /\/collections\/[^\/]*-([a-f0-9]{32})(?:[/?#]|$)/i },
    ],
};

const ITEM_SEARCH_REGEX: Record<string, RegExp> = {
    europeana: /\/item(\/\d+\/[^/?#]+)(?:[/?#]|$)/i,
};

const persistentModalState = {
    selectedExternalSource: 'artetra',
    externalSourceFilter: '',
    collectionSearch: '',
    cachedExternalArtworks: {} as Record<string, Artwork[]>,
    /** True when the current cache for a source came from "Search by link" (collection/item search). */
    artworksFromCollectionSearchBySource: {} as Record<string, boolean>,
};

function extractEuropeanaQf(value: string): string[] {
    try {
        const parsedUrl = new URL(value);
        return parsedUrl.searchParams.getAll('qf').filter(Boolean);
    } catch {
        const queryString = value.split('?')[1]?.split('#')[0];
        return queryString ? new URLSearchParams(queryString).getAll('qf').filter(Boolean) : [];
    }
}

export function extractExternalCollectionSearch(source: string, value: string): ExternalCollectionSearch | null {
    const patterns = COLLECTION_SEARCH_PATTERNS[source] || [];
    for (const pattern of patterns) {
        const match = value.match(pattern.regex);
        if (match) {
            return {
                collectionId: pattern.buildCollectionId ? pattern.buildCollectionId(match) : match[1],
                qf: source === 'europeana' ? extractEuropeanaQf(value) : [],
            };
        }
    }

    return null;
}

export function buildExternalAvailabilitySuccessMessage(kind: 'Artworks' | 'Collections' | 'Item'): string {
    return `${kind} searched successfully. Some artworks may not be displayed if they are unavailable for import.`;
}

export function buildExternalItemFailureMessage(response: any): string {
    const skippedArtworks = response?.data?.skippedArtworks;
    const firstReason = Array.isArray(skippedArtworks)
        ? skippedArtworks.find((item: any) => item?.reason)?.reason
        : null;
    return firstReason || response?.message || 'Failed to search item';
}

export type UseExternalSourceStateArgs = {
    apiClient: {
        searchExtenralArtworks: (source: string, filter: string, includeSounds?: boolean) => Promise<{ data: { artworks?: Artwork[] }; status: boolean; message?: string }>;
        searchCollections: (source: string, collectionId: string, options?: { qf?: string[]; includeSounds?: boolean }) => Promise<{ data: { artworks?: Artwork[]; skippedArtworks?: unknown[] }; status: boolean; message?: string }>;
        searchItem: (source: string, itemId: string, includeSounds?: boolean) => Promise<{ data: { artworks?: Artwork[]; skippedArtworks?: unknown[] }; status: boolean; message?: string }>;
    };
    show: boolean;
    artetraArtworks: Artwork[];
    includeEuropeanaSounds?: boolean;
    setArtworks: React.Dispatch<React.SetStateAction<Artwork[]>>;
    setFilteredArtworks: React.Dispatch<React.SetStateAction<Artwork[]>>;
    setSelectedArtwork: (artwork: Artwork | null) => void;
};

function successNotify(msg: string) {
    toast.success(msg, { position: 'bottom-left' });
}

function failedNotify(msg: string) {
    toast.error(msg, { position: 'bottom-left' });
}

export function useExternalSourceState({
    apiClient,
    show,
    artetraArtworks,
    includeEuropeanaSounds = false,
    setArtworks,
    setFilteredArtworks,
    setSelectedArtwork,
}: UseExternalSourceStateArgs) {
    const [selectedExternalSource, setSelectedExternalSourceState] = useState<string>(
        () => persistentModalState.selectedExternalSource
    );
    const [externalSourceFilter, setExternalSourceFilter] = useState<string>(
        () => persistentModalState.externalSourceFilter
    );
    const [collectionSearch, setCollectionSearch] = useState<string>(
        () => persistentModalState.collectionSearch
    );
    const [loadingExternalSource, setLoadingExternalSource] = useState(false);
    const [collectionSearchLoading, setCollectionSearchLoading] = useState(false);
    /** True when the current artworks list came from "Search by link" (collection/item search). */
    const [artworksFromCollectionSearch, setArtworksFromCollectionSearch] = useState(false);

    useEffect(() => {
        persistentModalState.selectedExternalSource = selectedExternalSource;
    }, [selectedExternalSource]);

    const bulkListAvailable =
        selectedExternalSource === 'artetra' || artworksFromCollectionSearch;

    useEffect(() => {
        persistentModalState.externalSourceFilter = externalSourceFilter;
    }, [externalSourceFilter]);

    useEffect(() => {
        persistentModalState.collectionSearch = collectionSearch;
    }, [collectionSearch]);

    useEffect(() => {
        if (!show) return;
        if (selectedExternalSource === 'artetra') {
            setArtworks(artetraArtworks.map((a) => a));
            setFilteredArtworks(artetraArtworks.filter((a) => !a.onsale));
            setArtworksFromCollectionSearch(false);
        } else {
            const cached = persistentModalState.cachedExternalArtworks[selectedExternalSource];
            if (cached?.length) {
                setArtworks(cached);
                setFilteredArtworks(cached);
                setArtworksFromCollectionSearch(
                    persistentModalState.artworksFromCollectionSearchBySource[selectedExternalSource] ?? false
                );
            } else {
                setArtworks([]);
                setFilteredArtworks([]);
                setArtworksFromCollectionSearch(false);
            }
        }
    }, [show, selectedExternalSource, artetraArtworks, setArtworks, setFilteredArtworks]);

    const onChangeExternalSource = useCallback(
        (source: string) => {
            if (source === 'artetra') {
                setArtworks(artetraArtworks.map((a) => a));
                setFilteredArtworks(artetraArtworks.filter((a) => !a.onsale));
                setArtworksFromCollectionSearch(false);
            } else {
                const cached = persistentModalState.cachedExternalArtworks[source];
                if (cached?.length) {
                    setFilteredArtworks(cached);
                    setArtworksFromCollectionSearch(
                        persistentModalState.artworksFromCollectionSearchBySource[source] ?? false
                    );
                } else {
                    setFilteredArtworks([]);
                    setArtworksFromCollectionSearch(false);
                }
            }
            setSelectedArtwork(null);
            setSelectedExternalSourceState(source);
            persistentModalState.selectedExternalSource = source;
        },
        [artetraArtworks, setArtworks, setFilteredArtworks, setSelectedArtwork]
    );

    const searchArtworks = useCallback(async () => {
        setLoadingExternalSource(true);
        try {
            const { data, status } = await apiClient.searchExtenralArtworks(
                selectedExternalSource,
                externalSourceFilter,
                selectedExternalSource === 'europeana' && includeEuropeanaSounds
            );
            if (status) {
                successNotify(buildExternalAvailabilitySuccessMessage('Artworks'));
                const list = data.artworks ?? [];
                setArtworks(list);
                setFilteredArtworks(list.filter((a) => !a.onsale));
                persistentModalState.cachedExternalArtworks[selectedExternalSource] = list;
                persistentModalState.artworksFromCollectionSearchBySource[selectedExternalSource] = false;
                setArtworksFromCollectionSearch(false);
            } else {
                failedNotify('Failed to search artworks');
            }
        } catch (error) {
            console.error(error);
            failedNotify('Failed to search artworks');
        } finally {
            setLoadingExternalSource(false);
        }
    }, [apiClient, selectedExternalSource, externalSourceFilter, includeEuropeanaSounds, setArtworks, setFilteredArtworks]);

    const searchCollections = useCallback(async () => {
        setCollectionSearchLoading(true);
        try {
            const collectionSearchResult = extractExternalCollectionSearch(selectedExternalSource, collectionSearch);
            const collectionId = collectionSearchResult?.collectionId || null;

            if (collectionId) {
                const { status, data } = await apiClient.searchCollections(
                    selectedExternalSource,
                    collectionId,
                    {
                        qf: collectionSearchResult?.qf,
                        includeSounds: selectedExternalSource === 'europeana' && includeEuropeanaSounds,
                    }
                );
                if (status && data.artworks?.length) {
                    successNotify(buildExternalAvailabilitySuccessMessage('Collections'));
                    setArtworks(data.artworks);
                    setFilteredArtworks(data.artworks.filter((a) => !a.onsale));
                    persistentModalState.cachedExternalArtworks[selectedExternalSource] =
                        data.artworks;
                    persistentModalState.artworksFromCollectionSearchBySource[selectedExternalSource] = true;
                    setArtworksFromCollectionSearch(true);
                } else {
                    failedNotify('Failed to search collections');
                }
            }

            const itemRegex = ITEM_SEARCH_REGEX[selectedExternalSource];
            const itemMatch = itemRegex ? itemRegex.exec(collectionSearch) : null;
            const itemId = itemMatch ? itemMatch[1] : null;

            if (itemId) {
                const itemResponse = await apiClient.searchItem(
                    selectedExternalSource,
                    itemId,
                    selectedExternalSource === 'europeana' && includeEuropeanaSounds
                );
                const { status, data } = itemResponse;
                if (status && data.artworks?.length) {
                    successNotify(buildExternalAvailabilitySuccessMessage('Item'));
                    setArtworks(data.artworks);
                    setFilteredArtworks(data.artworks.filter((a) => !a.onsale));
                    persistentModalState.cachedExternalArtworks[selectedExternalSource] =
                        data.artworks;
                    persistentModalState.artworksFromCollectionSearchBySource[selectedExternalSource] = true;
                    setArtworksFromCollectionSearch(true);
                } else {
                    failedNotify(buildExternalItemFailureMessage(itemResponse));
                }
            }

            if (!collectionId && !itemId) {
                failedNotify('Invalid collection or item link provided');
            }
        } catch (error) {
            console.error(error);
            failedNotify('Failed to search collections');
        } finally {
            setCollectionSearchLoading(false);
        }
    }, [
        apiClient,
        selectedExternalSource,
        collectionSearch,
        includeEuropeanaSounds,
        setArtworks,
        setFilteredArtworks,
    ]);

    return {
        selectedExternalSource,
        externalSourceFilter,
        setExternalSourceFilter,
        collectionSearch,
        setCollectionSearch,
        loadingExternalSource,
        collectionSearchLoading,
        bulkListAvailable,
        onChangeExternalSource,
        searchArtworks,
        searchCollections,
    };
}
